import FeedbackIcon from '@mui/icons-material/Feedback';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grow,
    IconButton,
    Snackbar,
    TextField,
    Tooltip,
    useMediaQuery,
    useTheme
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useError } from '../context/ErrorContext.tsx';
import { useUser } from '../context/UserContext.tsx';
import { submitFeedback } from '../services/feedbackService.ts';

interface FeedbackButtonProps {
  hasError?: boolean;
}

const FeedbackButton: React.FC<FeedbackButtonProps> = ({ hasError: propHasError = false }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [isBreaking, setIsBreaking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isCollectPage, setIsCollectPage] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const textFieldRef = useRef<HTMLInputElement>(null);
  
  // Get auth state and location
  const { loading: isLoading, user } = useUser();
  const location = useLocation();
  
  // Determine if we're on the app by checking the pathname
  const isApp = location.pathname === '/app' || location.pathname.startsWith('/app/');
  
  const { hasError: contextHasError } = useError();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Use either prop or context error state
  const hasError = propHasError || contextHasError;

  // Check if we're on a collect page
  useEffect(() => {
    const checkCollectPage = () => {
      setIsCollectPage(document.body.classList.contains('collect-page'));
    };
    
    // Check initially
    checkCollectPage();
    
    // Set up observer to watch for class changes on body
    const observer = new MutationObserver(checkCollectPage);
    observer.observe(document.body, { 
      attributes: true, 
      attributeFilter: ['class'] 
    });
    
    return () => observer.disconnect();
  }, []);

  // Animate button if hasError is true
  useEffect(() => {
    if (hasError) {
      setIsAnimating(true);
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [hasError]);

  // Multiple approaches to ensure focus works
  useEffect(() => {
    if (open) {
      // Use three different timing approaches to maximize chances of focus working
      // Immediate focus attempt
      if (textFieldRef.current) {
        textFieldRef.current.focus();
      }
      
      // Short delay focus attempt (for after initial render)
      const timer1 = setTimeout(() => {
        if (textFieldRef.current) {
          textFieldRef.current.focus();
        }
      }, 50);
      
      // Longer delay focus attempt (for after animations complete)
      const timer2 = setTimeout(() => {
        if (textFieldRef.current) {
          textFieldRef.current.focus();
        }
      }, 300);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [open]);

  const handleOpen = () => {
    setOpen(true);
    // Also try to focus when opening
    setTimeout(() => {
      if (textFieldRef.current) {
        textFieldRef.current.focus();
      }
    }, 100);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = async () => {
    if (!message.trim()) return;

    try {
      setIsSubmitting(true);
      await submitFeedback({
        message: message.trim(),
        is_breaking: isBreaking
      });
      setMessage('');
      setIsBreaking(false);
      setOpen(false);
      setShowSuccess(true);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
  };

  // Function to handle key press in dialog
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey && message.trim()) {
      handleSubmit();
    }
  };

  // Don't render anything while auth is loading or on collect pages
  if (isLoading || isCollectPage) {
    return null;
  }

  return (
    <>
      <Tooltip title="Send Feedback" placement={isMobile ? "top" : "left"} arrow>
        <Box
          sx={{
            position: 'fixed',
            zIndex: 1000,
            ...(isApp && user && isMobile
              ? {
                  // When authenticated or on app, position above bottom bar
                  bottom: isMobile ? '80px' : '100px',
                  right: '20px',
                }
              : {
                  // When not authenticated, position in bottom right corner
                  bottom: '20px',
                  right: '20px',
                }),
            transition: 'all 0.3s ease',
          }}
          className="feedback-button"
        >
          <IconButton
            ref={buttonRef}
            onClick={handleOpen}
            color="primary"
            size={isMobile ? "medium" : "large"}
            sx={{
              bgcolor: 'white',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              transition: 'all 0.3s',
              animation: isAnimating ? 'pulse 1s infinite' : 'none',
              ...(isMobile && {
                width: '36px',
                height: '36px',
              }),
              '&:hover': {
                bgcolor: '#f0f0f0',
                transform: 'scale(1.05)'
              },
              '@keyframes pulse': {
                '0%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.7)' },
                '70%': { boxShadow: '0 0 0 10px rgba(25, 118, 210, 0)' },
                '100%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)' }
              }
            }}
          >
            <FeedbackIcon sx={isMobile ? { fontSize: '1.2rem' } : undefined} />
          </IconButton>
        </Box>
      </Tooltip>

      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="sm" 
        fullWidth
        onKeyDown={handleKeyPress}
        // This prop can help with focus issues
        disableRestoreFocus
      >
        <DialogTitle>Send Feedback</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1 }}>
            <TextField
              // Use both autoFocus and ref
              autoFocus
              inputRef={textFieldRef}
              label="Your feedback"
              multiline
              rows={4}
              fullWidth
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              variant="outlined"
              placeholder="Tell us what's on your mind..."
              // Additional focus handling
              InputProps={{
                onFocus: (e) => {
                  // Ensure cursor goes to end of text
                  const input = e.target as HTMLInputElement;
                  const length = input.value.length;
                  input.setSelectionRange(length, length);
                }
              }}
            />
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={isBreaking}
                onChange={(e) => setIsBreaking(e.target.checked)}
                color="primary"
              />
            }
            label="Something is broken"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!message.trim() || isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Send Feedback'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showSuccess}
        autoHideDuration={5000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Grow}
      >
        <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
          Thank you for your feedback!
        </Alert>
      </Snackbar>
    </>
  );
};

export default FeedbackButton; 